import * as React from 'react';
import {useDispatch, useStore} from 'react-redux';

import {change, getFormValues} from 'redux-form';

import {sortByPosition} from 'nutshell-core/utils/sorting/sort-by-position';

import type {
    EmailSequenceCreateInput,
    SessionUser as SessionUserFragment,
    RelatedEmailAddresses as RelatedEmailAddressesFragment,
    EntityRelatedEmailAddresses as EntityRelatedEmailAddressesFragment,
    EmailableLead,
    GetSuggestedReplies_suggestedReplies as SuggestedReplies,
} from 'nutshell-graphql-types';

import {addNotification} from '../../../../notifications/index';
import type {NutshellState} from '../../../../store';

import {useCreateEmailSequence} from '../../graphql/hooks/use-create-email-sequence';
import {useGetEmailTemplateGroups} from '../../templates/template-groups/graphql/hooks';
import type {Template} from '../../templates/template-groups/types';

import {flattenUnmaterializedTemplatesInSequence} from '../flatten-templates-in-sequence';
import {CREATE_FROM_SCRATCH_ID, FORM_NAME} from '../types';
import type {OnSubmitArgs, EmailComposerFormStateNew} from '../types';

import {EmailSenderFormNew, DELAY_ERROR} from './email-sender-form-new';
import {getGroupSequenceTemplates} from './helpers';

type Props = {
    initialValues?: EmailComposerFormStateNew;
    entity?: EntityRelatedEmailAddressesFragment;
    relatedEmailAddresses: RelatedEmailAddressesFragment;
    relatedLeads: EmailableLead[];
    user: SessionUserFragment;
    closeModal: () => void;
    shouldRestoreFromStorage: boolean;
    hideTemplates?: boolean;
    emailReplyId?: string;
    createFromScratchTemplate: any;
    onEmailEdited: () => void;
    suggestedReplies?: SuggestedReplies;
    isLoadingSuggestedReplies?: boolean;
    isBodyFocused?: boolean;
    replyQuotedSuffixHtml?: string | null;
};

function formatEmailCount(count: number): string {
    return count === 1 ? '1 email' : `${count} emails`;
}

function formatFollowUpsCount(count: number): string {
    return count === 1 ? '1 follow-up' : `${count} follow-ups`;
}

export function EmailSenderGraphqlNew(props: Props) {
    const dispatch = useDispatch();
    const store = useStore<NutshellState>();
    const {createEmailSequence, isCreating, hasCreated} = useCreateEmailSequence();
    const [searchValue, setSearchValue] = React.useState<string>('');

    const {groups, uncategorized, recentlySent, isLoading, errorMessage} =
        useGetEmailTemplateGroups({search: searchValue});
    const onSetSequences = (newSequences: any) => {
        dispatch(change(FORM_NAME, 'sequences', newSequences));
    };
    const onSetTemplateGroups = (newGroups: any) => {
        dispatch(change(FORM_NAME, 'templateGroups', newGroups));
    };
    const onSetUncategorizedTemplates = (newTemplates: any) => {
        dispatch(change(FORM_NAME, 'uncategorizedTemplates', newTemplates));
    };
    const onSetRecentlySentTemplates = (newTemplates: any) => {
        dispatch(change(FORM_NAME, 'recentlySentTemplates', newTemplates));
    };

    /**
     * When we get the groups and other sequences from the query, we need to add
     * those to the sequences state, not overwriting any materialized data or
     * user changes. We then set the form state for groups, uncategorized, and
     * recents to the fetched sequences.
     */
    React.useEffect(() => {
        if (groups.length > 0 || uncategorized.length > 0 || recentlySent.length > 0) {
            const groupSequenceTemplates = getGroupSequenceTemplates(groups);

            // Combine all unmaterialized sequences into one array, including the create from scratch template
            const unmaterializedSequenceTemplatesFromServer = [
                props.createFromScratchTemplate[CREATE_FROM_SCRATCH_ID],
                ...groupSequenceTemplates,
                ...uncategorized,
                ...recentlySent,
            ];

            const currentValues = getFormValues(FORM_NAME)(store.getState());
            const sequencesFromFormState =
                (currentValues && (currentValues as {sequences?: unknown}).sequences) || {};

            // Take our existing sequences in form state (materialized)
            // and add any unmaterialized sequences we don't know about
            const rehydratedFlattenedSequences = unmaterializedSequenceTemplatesFromServer.reduce(
                (accum: Record<string, unknown>, template: Template) => {
                    if (!accum[template.id]) {
                        accum[template.id] = flattenUnmaterializedTemplatesInSequence(template);
                    }

                    return accum;
                },
                {...(sequencesFromFormState as Record<string, unknown>)}
            );

            onSetSequences(rehydratedFlattenedSequences);
            onSetTemplateGroups(groups);
            onSetRecentlySentTemplates(recentlySent);
            onSetUncategorizedTemplates(uncategorized);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups, uncategorized, recentlySent]);

    return (
        <EmailSenderFormNew
            {...props}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            isLoadingTemplates={isLoading}
            templateListErrorMessage={errorMessage}
            initialValues={
                props.shouldRestoreFromStorage || isCreating || hasCreated
                    ? undefined
                    : props.initialValues
            }
            relatedLeads={props.relatedLeads}
            // @ts-expect-error
            onSubmit={({form, sequenceId, shouldNotify}: OnSubmitArgs) => {
                let addresses = form.toAddresses;
                if (form.reply) {
                    addresses = form.reply.isReplyAll
                        ? form.reply.replyAllAddresses
                        : form.reply.toAddresses;
                }

                // Check for delay validation errors before proceeding
                // @ts-expect-error
                const emails = sortByPosition(form.sequences[sequenceId].emailTemplates);
                for (let index = 0; index < emails.length; index++) {
                    const email = emails[index];
                    const isFirstEmail = index === 0;

                    // Follow-up emails must have a delay. This is a safety
                    // check to prevent emails from being sent out all at once.
                    // Jira: https://nutshell.atlassian.net/browse/NUT-27538
                    // @ts-expect-error
                    if (!isFirstEmail && (!email.delay || email.delay.timespan === 0)) {
                        return Promise.reject(new Error(DELAY_ERROR));
                    }
                }

                const input: EmailSequenceCreateInput = {
                    emailSequence: {
                        emailSequenceTemplateId:
                            sequenceId === CREATE_FROM_SCRATCH_ID ? null : sequenceId,
                        toEmailAddresses: addresses.map((address) => ({
                            address: address.value.address,
                            display: address.value.display,
                        })),
                        ccEmailAddresses: form.ccAddresses.map((address) => ({
                            address: address.emailAddress,
                            display: address.display,
                        })),
                        userEmailId: form.fromAddress.id,
                        relatedLeadIds: form.relatedLeadIds,
                        emails: emails.map((email) => ({
                            emailTemplateId: email.id === CREATE_FROM_SCRATCH_ID ? null : email.id,
                            // By the time we're creating, we have a subject, but this appeases flow
                            subject: email.subject || '',
                            // By the time we're creating, we have a body, but this appeases flow
                            formattedBody: email.body || '',
                            delay: email.delay || {
                                unit: 'bzd',
                                timespan: 0,
                            },
                            // By the time we're creating, we have a value, but this appeases flow
                            sendAsReply: email.sendAsReply || false,
                            // When we are replying to an email, we only
                            // allow a single email. So, just set the
                            // email reply id to "all" emails
                            emailReplyId: props.emailReplyId,
                            attachments: email.fileAttachments
                                ? email.fileAttachments.map((file) => ({
                                      filename: file.filename,
                                      filesize: file.size,
                                      locationUri: file.url,
                                      mimeType: file.mimetype,
                                  }))
                                : [],
                        })),
                    },
                };

                return createEmailSequence(input).then((mutationResult) => {
                    if (
                        mutationResult &&
                        mutationResult.data &&
                        mutationResult.data.emailSequenceCreate
                    ) {
                        const {emailSequence} = mutationResult.data.emailSequenceCreate;

                        // Show user notification
                        if (shouldNotify) {
                            // The first email is always sent immediately (no delay),
                            // so count it as "sent". Only subsequent follow-up emails
                            // with delays are "scheduled". We intentionally don't use
                            // isReadyToSend here because it can intermittently be false
                            // due to race conditions (e.g. attachments still processing),
                            // leading to a misleading "0 emails sent" notification.
                            const totalEmails = emailSequence.emailsQueued.length;
                            const scheduledCount = Math.max(0, totalEmails - 1);

                            if (scheduledCount === 0 && totalEmails === 1) {
                                pushNotification(
                                    'Email sent',
                                    emailSequence.emailsQueued[0].subject,
                                    '',
                                    false
                                );
                            } else {
                                const sentCountMessage = formatEmailCount(1);
                                const scheduledCountMessage = formatFollowUpsCount(scheduledCount);

                                pushNotification(
                                    `${sentCountMessage} sent`,
                                    `${scheduledCountMessage} scheduled`,
                                    emailSequence.emailsQueued[0].id
                                );
                            }
                        }

                        return emailSequence.emailsQueued[0];
                    }
                });
            }}
        />
    );
}

const pushNotification = (
    title: string,
    body: string,
    filterEmailId?: string | null,
    isClickable = true
) => {
    function onClick() {
        if (isClickable) {
            // @ts-expect-error
            window.location = filterEmailId
                ? `/email/scheduled/${filterEmailId}`
                : '/email/scheduled';
        }
    }

    addNotification(title, {
        body,
        icon: 'email',
        onClick,
    });
};
